import { useEffect, useState } from "react";
import { Link, useSearch } from "wouter";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface Purchase {
  id: number;
  productName: string;
  productType: string;
  amountPaid: number;
  currency: string;
  downloadUrl: string | null;
  status: string;
}

export default function CheckoutSuccess() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const sessionId = params.get("session_id");
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Give webhook a moment to process, then fetch latest purchases
    const timer = setTimeout(() => {
      apiFetch<Purchase[]>("/api/checkout/purchases")
        .then((data) => setPurchases(data.filter((p) => p.status !== "failed")))
        .catch(console.error)
        .finally(() => setLoading(false));
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const latestPurchase = purchases[purchases.length - 1];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-lg w-full text-center">
        {/* Success icon */}
        <div className="w-20 h-20 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <p className="text-xs font-mono tracking-[0.3em] text-primary uppercase mb-2">
          Payment Confirmed
        </p>
        <h1 className="text-3xl font-black uppercase text-white mb-3">
          You're in.
        </h1>
        <p className="text-muted-foreground mb-8">
          Your purchase was successful. A receipt has been sent to your email by Stripe.
        </p>

        {loading ? (
          <div className="bg-card/60 border border-white/10 rounded-xl p-6 animate-pulse">
            <div className="h-4 bg-white/10 rounded w-3/4 mx-auto mb-3" />
            <div className="h-3 bg-white/10 rounded w-1/2 mx-auto" />
          </div>
        ) : latestPurchase ? (
          <div className="bg-card/60 border border-white/10 rounded-xl p-6 text-left mb-6">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
              {latestPurchase.productType.replace(/_/g, " ")}
            </p>
            <p className="text-white font-semibold text-lg mb-2">
              {latestPurchase.productName}
            </p>
            <p className="text-primary font-mono text-sm">
              ${(latestPurchase.amountPaid / 100).toFixed(2)}{" "}
              {latestPurchase.currency.toUpperCase()}
            </p>

            {latestPurchase.downloadUrl && (
              <a
                href={latestPurchase.downloadUrl}
                className="mt-4 flex items-center gap-2 text-primary hover:underline text-sm font-medium"
                download
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download your file
              </a>
            )}
          </div>
        ) : null}

        <div className="flex gap-3 justify-center">
          <Button asChild variant="outline" className="border-white/20 hover:bg-white/5">
            <Link href="/">Back to Home</Link>
          </Button>
          <Button asChild className="bg-primary text-primary-foreground">
            <Link href="/marketplace">Browse More</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
